/**
 * <legal-guarantee>
 *
 * Tooltip for the EU legal guarantee row in the cart drawer and on /cart.
 * - Hover-capable devices: opens on pointer enter, closes as soon as the
 *   pointer leaves the row or the panel.
 * - Click (touch and mouse) pins the panel open; clicking again closes it.
 * - Keyboard: opens on focus, closes on Escape or when focus leaves.
 * - Closes on any outside pointer down.
 */
if (!customElements.get('legal-guarantee')) {
  class LegalGuarantee extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.isPinned = false;
      this.ignoreNextFocus = false;
      this.closeTimer = null;
      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    }

    connectedCallback() {
      this.trigger = this.querySelector('[data-lg-trigger]');
      this.panel = this.querySelector('[data-lg-panel]');
      if (!this.trigger || !this.panel) return;

      this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      this.trigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.isPinned) {
          this.close();
        } else {
          this.open();
          this.isPinned = true;
        }
      });

      const closeButton = this.querySelector('[data-lg-close]');
      if (closeButton) {
        closeButton.addEventListener('click', () => this.close());
      }

      if (this.canHover) {
        // Listen on the trigger and the panel rather than the host: the host is
        // a full-width block so the panel can size against it, and hovering the
        // empty space next to the label should not open anything.
        [this.trigger, this.panel].forEach((element) => {
          element.addEventListener('pointerenter', () => {
            this.cancelScheduledClose();
            this.open();
          });
          element.addEventListener('pointerleave', () => {
            if (!this.isPinned) this.scheduleClose();
          });
        });
      }

      this.addEventListener('focusin', () => {
        if (this.ignoreNextFocus) return;
        this.open();
      });
      this.addEventListener('focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.close();
      });

      const image = this.querySelector('[data-lg-image]');
      if (image && !image.complete) {
        image.addEventListener('load', () => {
          if (this.isOpen) this.position();
        });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      this.cancelScheduledClose();
    }

    scheduleClose() {
      this.cancelScheduledClose();
      // Short grace period so the pointer can travel from the label to the panel.
      this.closeTimer = setTimeout(() => this.close(), 120);
    }

    cancelScheduledClose() {
      if (!this.closeTimer) return;
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    open() {
      this.cancelScheduledClose();
      if (this.isOpen) return;
      this.isOpen = true;
      this.panel.hidden = false;
      this.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.position();
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      document.addEventListener('keydown', this.onDocumentKeydown);
    }

    close() {
      this.cancelScheduledClose();
      if (!this.isOpen) return;
      this.isOpen = false;
      this.isPinned = false;
      this.panel.hidden = true;
      this.classList.remove('is-open', 'legal-guarantee--below');
      this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      document.removeEventListener('keydown', this.onDocumentKeydown);
    }

    /** Flip the panel below the row when there is not enough room above it. */
    position() {
      this.classList.remove('legal-guarantee--below');
      const spaceAbove = this.getBoundingClientRect().top;
      if (spaceAbove < this.panel.offsetHeight + 24) {
        this.classList.add('legal-guarantee--below');
      }
    }

    onDocumentPointerDown(event) {
      if (this.contains(event.target)) return;
      this.close();
    }

    onDocumentKeydown(event) {
      if (event.key !== 'Escape') return;
      this.close();
      // Returning focus to the trigger must not re-open the panel.
      this.ignoreNextFocus = true;
      this.trigger.focus();
      window.requestAnimationFrame(() => {
        this.ignoreNextFocus = false;
      });
    }
  }

  customElements.define('legal-guarantee', LegalGuarantee);
}
